import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Bus, LayoutDashboard, Users, ScanLine, FileText, 
  LogOut, Menu, X, Settings, MapPin, Truck, Route,
  Package, Receipt, BarChart3, Award, Smartphone, Building2,
  Activity, Printer, Navigation, History
} from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';

export const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, isOverseer, isDriver } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  
  const isDriverOnly = isDriver ? isDriver() : user?.role === 'driver';

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/dashboard/scanner', icon: ScanLine, label: 'QR Scanner' },
    { path: '/dashboard/search', icon: Users, label: 'Search Passengers' },
    { path: '/dashboard/luggage', icon: Package, label: 'Luggage' },
    { path: '/dashboard/luggage-dashboard', icon: MapPin, label: 'Luggage Dashboard' },
    { path: '/dashboard/luggage-history', icon: History, label: 'Luggage History' },
    { path: '/dashboard/expenses', icon: Receipt, label: 'Expenses' },
    { path: '/dashboard/loyalty', icon: Award, label: 'Loyalty' },
  ];
  
  const driverItems = [
    { path: '/dashboard/driver', icon: Smartphone, label: 'Driver PWA' },
    { path: '/dashboard/scanner', icon: ScanLine, label: 'QR Scanner' },
  ];
  
  const superuserItems = [
    { path: '/dashboard/reports', icon: BarChart3, label: 'P&L Reports' },
  ];

  const adminItems = [
    { path: '/admin', icon: Settings, label: 'Admin Dashboard' },
      { path: '/admin/integrations', icon: Settings, label: 'Integrations' },
    { path: '/admin/stations', icon: Building2, label: 'Manage Stations' },
    { path: '/admin/routes', icon: Route, label: 'Manage Routes' },
    { path: '/admin/buses', icon: Truck, label: 'Manage Buses' },
    { path: '/admin/trips', icon: MapPin, label: 'Manage Trips' },
    { path: '/admin/users', icon: Users, label: 'Manage Users' },
    { path: '/admin/gps-tracking', icon: Navigation, label: 'GPS Tracking' },
    { path: '/admin/activity-logs', icon: Activity, label: 'Activity Logs' },
    { path: '/admin/print-history', icon: Printer, label: 'Print History' },
  ];

  // Select navigation items based on role
  const getNavItems = () => {
    if (isDriverOnly) {
      return driverItems;
    }
    return navItems;
  };

  const getVisibleAdminItems = () => {
    if (isOverseer()) return adminItems;
    if (user?.role === 'station_superuser') {
      const duties = user?.delegated_duties || [];
      if (duties.length === 0) return [];
      return adminItems.filter(item => {
        if (item.path === '/admin') return true;
        if (item.path === '/admin/stations') return duties.includes('manage_stations');
        if (item.path === '/admin/routes') return duties.includes('manage_routes');
        if (item.path === '/admin/buses') return duties.includes('manage_buses');
        if (item.path === '/admin/trips') return duties.includes('manage_trips');
        if (item.path === '/admin/users') return duties.includes('manage_users');
        if (item.path === '/admin/gps-tracking') return duties.includes('gps_tracking');
        if (item.path === '/admin/activity-logs') return duties.includes('activity_logs');
        if (item.path === '/admin/print-history') return duties.includes('print_history');
          if (item.path === '/admin/integrations') return false;
        return false;
      });
    }
    return [];
  };

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden md:flex md:flex-col md:w-64 bg-panashe-blue text-white">
        <div className="p-4 border-b border-white/10">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="/panashe-logo.jpg" 
              alt="Panashe Coaches" 
              className="h-10 w-10 rounded-lg object-cover"
            />
            <div>
              <span className="font-heading text-lg font-bold">PANASHE</span>
              <span className="text-panashe-gold ml-1 font-heading">COACHES</span>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <p className="text-xs text-white/50 uppercase tracking-wider mb-3 px-3">
            {isDriverOnly ? 'Driver' : 'Station'}
          </p>
          {getNavItems().map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                isActive(item.path) 
                  ? 'bg-panashe-gold text-slate-900 font-medium' 
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </Link>
          ))}
          
          {/* Superuser-only items */}
          {(user?.role === 'station_superuser' || isOverseer()) && (
            <>
              <p className="text-xs text-white/50 uppercase tracking-wider mt-6 mb-3 px-3">Reports</p>
              {superuserItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                    isActive(item.path) 
                      ? 'bg-panashe-gold text-slate-900 font-medium' 
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Link>
              ))}
            </>
          )}

          {getVisibleAdminItems().length > 0 && (
            <>
              <p className="text-xs text-white/50 uppercase tracking-wider mt-6 mb-3 px-3">Admin</p>
              {getVisibleAdminItems().map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                    isActive(item.path) 
                      ? 'bg-panashe-gold text-slate-900 font-medium' 
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Link>
              ))}
            </>
          )}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-panashe-gold rounded-full flex items-center justify-center">
              <span className="text-slate-900 font-bold">
                {user?.full_name?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.full_name}</p>
              <p className="text-xs text-white/50 capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full border-white/20 text-white hover:bg-white/10"
            onClick={handleLogout}
            data-testid="sidebar-logout"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-panashe-blue text-white h-14 flex items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="/panashe-logo.jpg" 
            alt="Panashe" 
            className="h-8 w-8 rounded-lg object-cover"
          />
          <span className="font-heading font-bold">PANASHE</span>
        </Link>
        <button onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-14 bottom-0 w-64 bg-panashe-blue text-white overflow-y-auto animate-slide-in">
            <nav className="p-4 space-y-1">
              {getNavItems().map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md ${
                    isActive(item.path) 
                      ? 'bg-panashe-gold text-slate-900 font-medium' 
                      : 'text-white/70'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Link>
              ))}
              
              {(user?.role === 'station_superuser' || isOverseer()) && (
                <>
                  <p className="text-xs text-white/50 uppercase tracking-wider mt-4 mb-2 px-3">Reports</p>
                  {superuserItems.map(item => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md ${
                        isActive(item.path) 
                          ? 'bg-panashe-gold text-slate-900 font-medium' 
                          : 'text-white/70'
                      }`}
                    >
                      <item.icon className="h-5 w-5" />
                      {item.label}
                    </Link>
                  ))}
                </>
              )}
              
              {getVisibleAdminItems().length > 0 && (
                <>
                  <p className="text-xs text-white/50 uppercase tracking-wider mt-4 mb-2 px-3">Admin</p>
                  {getVisibleAdminItems().map(item => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md ${
                        isActive(item.path) 
                          ? 'bg-panashe-gold text-slate-900 font-medium' 
                          : 'text-white/70'
                      }`}
                    >
                      <item.icon className="h-5 w-5" />
                      {item.label}
                    </Link>
                  ))}
                </>
              )}
            </nav>
            
            <div className="p-4 border-t border-white/10">
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full border-white/20 text-white"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 md:pt-0 pt-14 min-w-0 max-w-full overflow-x-hidden">
        <main className="p-3 sm:p-4 md:p-6 w-full max-w-full">
          {children}
        </main>
      </div>
    </div>
  );
};
